// A agenda.
//
// Ela se preenche sozinha: quem você responde entra como contato. Esta tela
// serve para VER, corrigir e apagar — não para cadastrar em massa, que é
// trabalho que ninguém faz.
//
// E DESDE 30/10/2026 ELA TAMBÉM RECEBE AGENDA MIGRADA, que é outra coisa.
// Contato capturado ao enviar SEMPRE tem endereço — foi por um e-mail que
// ele entrou. Contato que veio da agenda de outro provedor pode ter só nome
// e telefone: medido no volpato.ind.br, 1.340 dos 1.837 são assim. Por isso
// nada nesta tela pode supor que o endereço existe.
//
// A separação entre "meus contatos" e "pessoas com quem você já falou" é a
// mesma que o Gmail faz, e existe por um motivo prático: depois de alguns
// meses a captura automática junta muito endereço de uma vez só, e sem a
// separação a agenda de verdade se perde no meio.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as contatos from "../api/contatos";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { corDoAvatar, dataCurta, inicial, nomeCurto } from "../ui/formato";

/**
 * O que aparece embaixo do nome.
 *
 * Era sempre o endereço. Com a agenda migrada ele pode não existir, e uma
 * linha em branco embaixo do nome parece contato quebrado — quando na
 * verdade é um contato de telefone, inteiro.
 *
 * A ordem é a do que serve para ALCANÇAR a pessoa: endereço primeiro porque
 * é um webmail; telefone quando não há endereço; empresa quando não há nem
 * um nem outro, porque ainda diz quem é.
 */
export function linhaSecundaria(c: {
  email?: string | null;
  telefone?: string | null;
  empresa?: string | null;
}): string {
  const email = (c.email ?? "").trim();
  if (email) return email;
  const telefone = (c.telefone ?? "").trim();
  if (telefone) return telefone;
  return (c.empresa ?? "").trim();
}

/**
 * A cor do avatar vem do endereço — e, sem ele, do nome.
 *
 * `corDoAvatar` devolve a MESMA cor para entrada vazia. Com a agenda
 * migrada isso pintaria 1.340 contatos de uma cor só, e o avatar existe
 * justamente para a pessoa reconhecer a linha de relance.
 */
export function chaveDaCor(c: { email?: string | null; nome?: string | null }): string {
  return ((c.email ?? "").trim() || (c.nome ?? "").trim());
}

/**
 * Os grupos presentes na agenda, em ordem de tamanho.
 *
 * Por tamanho e não alfabética porque o grupo grande é o que a pessoa usa
 * para navegar; o de um membro só é exceção e fica no fim.
 *
 * O grupo que contém TODO MUNDO fica de fora do filtro. O Roundcube e os
 * sincronizadores de CardDAV criam um grupo assim (`myContacts`, com os
 * 1.699 contatos da diana@volpato.ind.br — todos), e um filtro que não
 * filtra nada só ocupa a barra. Ele continua no contato, visível como chip:
 * o que se esconde é o atalho inútil, não o dado.
 */
export function gruposDaAgenda(
  itens: { grupos?: string[] | null }[],
): { nome: string; quantos: number }[] {
  const conta = new Map<string, number>();
  for (const c of itens) {
    for (const g of c.grupos ?? []) {
      const nome = g.trim();
      if (nome) conta.set(nome, (conta.get(nome) ?? 0) + 1);
    }
  }
  return [...conta.entries()]
    .filter(([, quantos]) => quantos < itens.length || itens.length <= 1)
    .map(([nome, quantos]) => ({ nome, quantos }))
    .sort((a, b) => b.quantos - a.quantos || a.nome.localeCompare(b.nome));
}

export function Contatos() {
  const fila = useQueryClient();
  const [termo, setTermo] = useState("");
  const [editando, setEditando] = useState<string | null>(null);
  const [grupo, setGrupo] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ["contatos", termo, grupo],
    queryFn: ({ signal }) => contatos.listar({ q: termo || undefined, grupo: grupo ?? undefined }, signal),
  });

  // Os grupos saem da agenda INTEIRA, não da filtrada: com um grupo
  // escolhido, a lista volta só com ele, e a barra perderia os outros —
  // a pessoa ficaria presa no filtro sem caminho de volta.
  const todos = useQuery({
    queryKey: ["contatos", "", null],
    queryFn: ({ signal }) => contatos.listar({}, signal),
  });
  const grupos = gruposDaAgenda(todos.data?.itens ?? []);

  const itens = lista.data?.itens ?? [];
  const meus = itens.filter((c) => !c.automatico);
  const conhecidos = itens.filter((c) => c.automatico);

  const invalidar = () => void fila.invalidateQueries({ queryKey: ["contatos"] });

  return (
    <div className="flex-1 overflow-y-auto rounded-tl-2xl bg-white">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <h1 className="text-xl text-tinta">Contatos</h1>
        <p className="mt-1 text-sm text-tinta-fraca">
          Quem você responde entra aqui sozinho. Contatos trazidos de outro
          provedor também aparecem, inclusive os que só têm telefone.
        </p>

        <input
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Procurar por nome, endereço, telefone ou empresa"
          aria-label="Procurar contatos"
          className="mt-4 w-full rounded-lg border border-linha px-3 py-2 text-sm"
        />

        {grupos.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setGrupo(null)}
              className={`rounded-full px-3 py-1 text-xs ${
                grupo === null ? "bg-acao text-white" : "border border-linha text-tinta-fraca"
              }`}
            >
              Todos
            </button>
            {grupos.map((g) => (
              <button
                key={g.nome}
                onClick={() => setGrupo(grupo === g.nome ? null : g.nome)}
                className={`rounded-full px-3 py-1 text-xs ${
                  grupo === g.nome ? "bg-acao text-white" : "border border-linha text-tinta-fraca"
                }`}
              >
                {g.nome} · {g.quantos}
              </button>
            ))}
          </div>
        )}

        {lista.isError && <p className="mt-4 text-sm text-perigo">{explicar(lista.error)}</p>}
        {!lista.isPending && itens.length === 0 && (
          <p className="mt-8 text-center text-sm text-tinta-fraca">
            {termo
              ? "Nenhum contato com esse trecho."
              : "A agenda ainda está vazia. Responda uma mensagem e o contato aparece aqui."}
          </p>
        )}

        {/* "MEUS CONTATOS" APARECE MESMO VAZIA.
            Apontamento 12 da revisão de UX: o texto da outra seção diz
            "Editar move para os seus contatos" e a seção não estava na
            tela. `Grupo` não desenhava nada quando vazio — e numa agenda só
            de captura automática, que é TODA agenda nova, era sempre o caso.
            O ajuste pedido foi exibir a seção mesmo vazia, e ele é melhor
            que reescrever a frase: a seção vazia ENSINA o modelo da agenda
            (há dois grupos, e editar passa um para o outro) em vez de só
            parar de mentir.
            `vazio` só vale quando existe ALGUMA coisa na agenda: numa agenda
            totalmente vazia a tela já diz isso acima, e duas seções vazias
            embaixo seriam ruído. */}
        <Grupo
          titulo="Meus contatos"
          explicacao="Salvos ou corrigidos por você. A captura automática não mexe mais neles."
          itens={meus}
          vazio={
            itens.length > 0
              ? "Nenhum ainda. Editar um contato abaixo o traz para cá."
              : undefined
          }
          editando={editando}
          aoEditar={setEditando}
          aoMudar={invalidar}
        />
        <Grupo
          titulo="Pessoas com quem você já falou"
          // O nome da seção é dito EXATO, porque agora ela está sempre lá e
          // a referência é conferível.
          explicacao='Entraram sozinhas quando você enviou uma mensagem. Editar move para "Meus contatos", acima.'
          itens={conhecidos}
          editando={editando}
          aoEditar={setEditando}
          aoMudar={invalidar}
        />
      </div>
    </div>
  );
}

function Grupo({
  titulo,
  explicacao,
  itens,
  vazio,
  editando,
  aoEditar,
  aoMudar,
}: {
  titulo: string;
  explicacao: string;
  itens: contatos.Contato[];
  /**
   * O que dizer quando a seção está vazia.
   *
   * Ausente, a seção não é desenhada — que é o certo para a de captura
   * automática. Presente, ela aparece vazia, porque a OUTRA seção aponta
   * para esta pelo nome e um apontamento para lugar inexistente é pior que
   * uma lista vazia.
   */
  vazio?: string;
  editando: string | null;
  aoEditar: (uuid: string | null) => void;
  aoMudar: () => void;
}) {
  if (itens.length === 0 && !vazio) return null;
  return (
    <section className="mt-8">
      <h2 className="text-sm font-medium text-tinta">{titulo}</h2>
      <p className="mb-2 text-xs text-tinta-fraca">{explicacao}</p>
      {itens.length === 0 ? (
        <p className="rounded-lg border border-dashed border-linha px-3 py-4 text-center text-xs text-tinta-fraca">
          {vazio}
        </p>
      ) : (
      <ul className="divide-y divide-linha-suave rounded-lg border border-linha">
        {itens.map((c) => (
          <Linha
            key={c.uuid}
            contato={c}
            editando={editando === c.uuid}
            aoEditar={() => aoEditar(editando === c.uuid ? null : (c.uuid ?? null))}
            aoFechar={() => aoEditar(null)}
            aoMudar={aoMudar}
          />
        ))}
      </ul>
      )}
    </section>
  );
}

function Linha({
  contato,
  editando,
  aoEditar,
  aoFechar,
  aoMudar,
}: {
  contato: contatos.Contato;
  editando: boolean;
  aoEditar: () => void;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const [campos, setCampos] = useState({
    nome: contato.nome ?? "",
    telefone: contato.telefone ?? "",
    empresa: contato.empresa ?? "",
  });

  const salvar = useMutation({
    mutationFn: () => contatos.alterar(contato.uuid!, campos),
    onSuccess: () => {
      aoFechar();
      aoMudar();
    },
  });

  const apagar = useMutation({
    mutationFn: () => contatos.remover(contato.uuid!),
    onSuccess: aoMudar,
  });

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-center gap-3">
        <span
          className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-xs text-white"
          style={{ background: corDoAvatar(chaveDaCor(contato)) }}
          aria-hidden="true"
        >
          {inicial(contato.nome, contato.email)}
        </span>
        <span className="min-w-0 flex-1 text-sm">
          <span className="block truncate text-tinta">
            {nomeCurto(contato.nome, contato.email)}
          </span>
          <span className="block truncate text-xs text-tinta-fraca">
            {linhaSecundaria(contato)}
          </span>
          {/* Telefone e empresa aparecem quando NÃO são a linha de cima:
              repetir o mesmo dado duas vezes só ocupa espaço. */}
          {/* Os grupos do contato. Ficam na própria linha porque é ali que
              a pessoa decide se é a pessoa certa — ir até a edição para
              descobrir em que grupo alguém está é um clique por contato. */}
          {!!contato.grupos?.length && (
            <span className="mt-0.5 flex flex-wrap gap-1">
              {contato.grupos.map((g) => (
                <span
                  key={g}
                  className="rounded-full bg-fundo-suave px-1.5 py-0.5 text-[10px] text-tinta-fraca"
                >
                  {g}
                </span>
              ))}
            </span>
          )}
          {(contato.telefone || contato.empresa) &&
            linhaSecundaria(contato) === (contato.email ?? "").trim() &&
            !!(contato.email ?? "").trim() && (
              <span className="block truncate text-xs text-tinta-fraca">
                {[contato.telefone, contato.empresa].filter(Boolean).join(" · ")}
              </span>
            )}
        </span>
        {/* Quantas vezes você escreveu, e quando. É o que diferencia o
            contato que importa do endereço que apareceu uma vez. */}
        {!!contato.interacoes && (
          <span className="hidden text-xs text-tinta-fraca sm:block">
            {contato.interacoes}{" "}
            {contato.interacoes === 1 ? "mensagem" : "mensagens"}
            {contato.ultima_interacao_em && ` · ${dataCurta(contato.ultima_interacao_em)}`}
          </span>
        )}
        {/* AÇÕES COMO BOTÃO, NÃO COMO PALAVRA SUBLINHADA.
            Eram duas de 12px colados, "apagar" a poucos pixels de "editar" —
            apontamento 13 da revisão de UX. Num telefone isso é apagar o
            contato querendo abri-lo.
            `flex-shrink-0` porque a coluna do meio é que deve encolher: sem
            ele, um nome comprido comia os botões. */}
        <span className="flex flex-shrink-0 gap-1.5">
          <button
            type="button"
            onClick={aoEditar}
            aria-expanded={editando}
            aria-label={`${editando ? "Fechar a edição de" : "Editar"} ${nomeCurto(contato.nome, contato.email)}`}
            className="min-h-[30px] rounded-full border border-linha px-2.5 py-1 text-xs text-acao hover:bg-acao-clara focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao"
          >
            {editando ? "fechar" : "editar"}
          </button>
          <button
            type="button"
            onClick={() => apagar.mutate()}
            disabled={apagar.isPending}
            aria-label={`Apagar o contato ${nomeCurto(contato.nome, contato.email)}`}
            className="min-h-[30px] rounded-full border border-perigo/30 px-2.5 py-1 text-xs text-perigo hover:bg-perigo/10 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-acao"
            title="Apagar não apaga e-mail nenhum, e o contato volta na próxima troca de mensagens."
          >
            apagar
          </button>
        </span>
      </div>

      {editando && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            salvar.mutate();
          }}
          className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]"
        >
          {(
            [
              ["nome", "Nome"],
              ["telefone", "Telefone"],
              ["empresa", "Empresa"],
            ] as const
          ).map(([campo, rotulo]) => (
            <input
              key={campo}
              value={campos[campo]}
              onChange={(e) => setCampos((c) => ({ ...c, [campo]: e.target.value }))}
              placeholder={rotulo}
              aria-label={rotulo}
              className="rounded-lg border border-linha px-3 py-2 text-sm"
            />
          ))}
          <button
            type="submit"
            disabled={salvar.isPending}
            className="flex items-center gap-2 rounded-full bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
          >
            <Icone nome="CheckCircle2" tamanho={16} />
            Salvar
          </button>
          {salvar.error && (
            <p className="text-sm text-perigo sm:col-span-4">{explicar(salvar.error)}</p>
          )}
        </form>
      )}
    </li>
  );
}
